import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Input, Button, Space, message, Table, Form, Typography } from 'antd';
import { UserAddOutlined, DeleteOutlined, EditOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { useWebSocket } from '../contexts/WebSocketProvider';
import { formatDistanceToNow } from 'date-fns';
import { useTranslation } from 'react-i18next';

interface User {
  username: string;
  createdAt: number;
}

interface UserManagementModalProps {
  visible: boolean;
  onClose: () => void;
}

const UserManagementModal: React.FC<UserManagementModalProps> = ({ visible, onClose }) => {
  const { t } = useTranslation();
  const { fetchUsers, addUser, editUser, deleteUser, addMessageListener, removeMessageListener } = useWebSocket();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [currentUser, setCurrentUser] = useState<string | null>(null);
  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);

  // Обработчик сообщений веб-сокета
  const handleWebSocketMessage = useCallback((data: any) => {
    if (data.type === 'users_list') {
      setUsers(data.users || []);
      setLoading(false);
    } else if (data.type === 'user_added') {
      message.success(t('userManagement.userAdded', { username: data.username }));
      setAddModalVisible(false);
      addForm.resetFields();
      // Обновить список пользователей
      fetchUsers();
    } else if (data.type === 'user_updated') {
      message.success(t('userManagement.userUpdated', { username: data.username }));
      setEditModalVisible(false);
      editForm.resetFields();
    } else if (data.type === 'user_deleted') {
      message.success(t('userManagement.userDeleted', { username: data.username }));
      // Обновить список пользователей
      fetchUsers();
    } else if (data.type === 'error' && data.message) {
      message.error(data.message);
      setLoading(false);
    }
  }, [addForm, editForm, fetchUsers]);

  // Регистрируем и отменяем регистрацию обработчика при монтировании/размонтировании
  useEffect(() => {
    // Регистрируем обработчик сообщений
    addMessageListener(handleWebSocketMessage);
    
    // Очистка при размонтировании
    return () => {
      removeMessageListener(handleWebSocketMessage);
    };
  }, [addMessageListener, removeMessageListener, handleWebSocketMessage]);

  // Загрузка списка пользователей при открытии модального окна
  useEffect(() => {
    if (visible) {
      setLoading(true);
      fetchUsers();
    }
  }, [visible, fetchUsers]);

  // Обработчик добавления пользователя
  const handleAddUser = (values: { username: string; password: string }) => {
    addUser(values.username, values.password);
  };

  // Обработчик редактирования пользователя
  const handleEditUser = (values: { password: string }) => {
    if (!currentUser) {
      message.error(t('userManagement.userNotSelected'));
      return;
    }
    editUser(currentUser, values.password);
  };

  // Обработчик удаления пользователя
  const handleDeleteUser = (username: string) => {
    console.log('Вызвана функция handleDeleteUser для пользователя:', username);
    
    setUserToDelete(username);
    setDeleteConfirmVisible(true);
  };

  // Обработчик подтверждения удаления
  const confirmDelete = () => {
    if (userToDelete) {
      console.log('Подтверждено удаление пользователя:', userToDelete);
      message.loading(t('userManagement.deletingUser'), 1);
      deleteUser(userToDelete);
      setDeleteConfirmVisible(false);
    }
  };

  // Открытие модального окна редактирования
  const showEditModal = (username: string) => {
    setCurrentUser(username);
    setEditModalVisible(true);
    editForm.resetFields();
  };

  // Фильтрация пользователей по поисковому запросу
  const filteredUsers = searchTerm
    ? users.filter(user => 
        user.username.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : users;

  // Колонки таблицы
  const columns = [
    {
      title: t('userManagement.username'),
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: t('userManagement.created'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt: number) => formatDistanceToNow(new Date(createdAt), { addSuffix: true })
    },
    {
      title: t('userManagement.actions'),
      key: 'actions',
      render: (_: any, record: User) => (
        <Space size="middle">
          <Button 
            icon={<EditOutlined />} 
            onClick={() => showEditModal(record.username)}
            title={t('userManagement.editPassword', { username: record.username })}
          />
          <Button 
            danger 
            icon={<DeleteOutlined />} 
            onClick={(e) => {
              e.stopPropagation(); // Предотвращаем всплытие события
              console.log('Нажата кнопка удаления для:', record.username);
              handleDeleteUser(record.username);
            }}
            title={t('userManagement.deleteUser', { username: record.username })}
          />
        </Space>
      ),
    },
  ];

  return (
    <Modal
      title={t('userManagement.userManagement')}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
    >
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <Space>
            <Input 
              placeholder={t('userManagement.searchUsername')} 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              prefix={<SearchOutlined />}
              allowClear
            />
            <Button 
              icon={<ReloadOutlined />} 
              onClick={() => {
                setLoading(true);
                fetchUsers();
              }}
              title={t('userManagement.updateList')}
            />
          </Space>
          <Button 
            type="primary" 
            icon={<UserAddOutlined />} 
            onClick={() => setAddModalVisible(true)}
          >
            {t('userManagement.addUser')}
          </Button>
        </div>
      </div>

      <Table 
        dataSource={filteredUsers} 
        columns={columns} 
        rowKey="username"
        loading={loading}
        locale={{ emptyText: t('userManagement.noData') }}
        pagination={{ pageSize: 10 }}
      />

      {/* Модальное окно добавления пользователя */}
      <Modal
        title={t('userManagement.addNewUser')}
        open={addModalVisible}
        onCancel={() => setAddModalVisible(false)}
        footer={null}
      >
        <Form
          form={addForm}
          layout="vertical"
          onFinish={handleAddUser}
        >
          <Form.Item
            name="username"
            label={t('userManagement.username')}
            rules={[{ required: true, message: t('userManagement.usernameRequired') }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="password"
            label={t('userManagement.password')}
            rules={[{ required: true, message: t('userManagement.passwordRequired') }]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {t('userManagement.addUser')}
              </Button>
              <Button onClick={() => setAddModalVisible(false)}>
                {t('userManagement.cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Модальное окно редактирования пользователя */}
      <Modal
        title={t('userManagement.changePassword', { username: currentUser })}
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditUser}
        >
          <Form.Item
            name="password"
            label={t('userManagement.newPassword')}
            rules={[{ required: true, message: t('userManagement.newPasswordRequired') }]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {t('userManagement.updatePassword')}
              </Button>
              <Button onClick={() => setEditModalVisible(false)}>
                {t('userManagement.cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Модальное окно подтверждения удаления пользователя */}
      <Modal
        title={t('userManagement.confirmDeletion')}
        open={deleteConfirmVisible}
        onCancel={() => setDeleteConfirmVisible(false)}
        okText={t('userManagement.delete')}
        okButtonProps={{ danger: true }}
        cancelText={t('userManagement.cancel')}
        onOk={confirmDelete}
      >
        {userToDelete && (
          <>
            <p>{t('userManagement.confirmDeletionContent', { username: userToDelete })}</p>
            <p style={{ color: '#ff4d4f' }}>
              <b>{t('userManagement.attention')}:</b> {t('userManagement.ifUserIsAuthorized')}
            </p>
          </>
        )}
      </Modal>
    </Modal>
  );
};

export default UserManagementModal; 